import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Appointment } from '~/Components/Icons/eventTypes/Appointment';
import { Class } from '~/Components/Icons/eventTypes/Class';
import { OpenSession } from '~/Components/Icons/eventTypes/OpenSession';
import { SpecialEvents } from '~/Components/Icons/eventTypes/SpecialEvents';
import { IItemCreationListItem } from '~/Components/ItemCreation/ItemCreationPicker';
import { useHasAccess, useIntegrations } from '~/Hooks';
import { AccessList } from '~/Hooks/useHasAccess/accessList.types';
import { EventType } from '~/Models/DTO/Event.model';
import { ExternalPartyID } from '~/Models/DTO/externalParties.model';

interface IUseEventTypePickerListItems {
  onEventTypeClicked: (eventType: EventType) => void;
}

export const useEventTypePickerListItems = ({
  onEventTypeClicked,
}: IUseEventTypePickerListItems): IItemCreationListItem[] => {
  const { t } = useTranslation();
  const { hasAccess } = useHasAccess();
  const { hasIntegration } = useIntegrations();
  const isUSCIntegrationEnabled = useMemo(() => hasIntegration(ExternalPartyID.USC_INTEGRATION), [
    hasIntegration,
  ]);

  const eventTypeItem = useMemo(
    () => ({
      class: {
        title: t('event.class'),
        testId: 'eventType.class',
        description: t('event.class.description.subtitle'),
        onClick: () => onEventTypeClicked(EventType.CLASS),
        icon: <Class />,
      },
      specialEvent: {
        title: t('event.specialEvent'),
        testId: 'eventType.specialEvent',
        description: t('event.specialEvent.description.subtitle'),
        onClick: () => onEventTypeClicked(EventType.SPECIAL_EVENT),
        icon: <SpecialEvents />,
      },
      appointment: {
        title: t('event.appointment'),
        testId: 'eventType.appointment',
        description: t('event.appointment.description.subtitle'),
        onClick: () => onEventTypeClicked(EventType.APPOINTMENT),
        icon: <Appointment />,
      },
      freeTraining: {
        title: t('event.freeTraining.open'),
        testId: 'eventType.freeTraining',
        description: t('event.freeTraining.description.subtitle'),
        onClick: () => onEventTypeClicked(EventType.FREE_TRAINING),
        icon: <OpenSession />,
        isNew: true,
      },
      openSession: {
        title: t('event.freeTraining.open'),
        testId: 'eventType.openSession',
        description: t('event.freeTraining.description.subtitle'),
        onClick: () => onEventTypeClicked(EventType.OPEN_SESSION),
        icon: <OpenSession />,
      },
    }),
    [t],
  );

  if (hasAccess(AccessList.ONLY_FREE_TRAINING)) {
    return [eventTypeItem.class, eventTypeItem.freeTraining];
  }

  if (isUSCIntegrationEnabled && hasAccess(AccessList.EDIT_FREE_TRAINING)) {
    return [eventTypeItem.class, eventTypeItem.specialEvent, eventTypeItem.freeTraining];
  }

  return [eventTypeItem.class, eventTypeItem.specialEvent];
};
